/**
 * Query service for MetricData Dev Tool
 * Handles GraphQL query execution with pagination and filtering
 *
 * @module MetricDataDevTool/services/queryService
 */

import { API, graphqlOperation } from 'aws-amplify';
import { metricDataByClientDate, metricDataByClientStoreDateMetric } from 'graphql/queries';
import { QUERY_CONFIG } from '../constants';

// Threshold for switching between query strategies
// TODO: Temporarily set to 0 to always use bulk query until per-metric query is fixed
const METRIC_QUERY_THRESHOLD = 0; // If selecting fewer than this, use per-metric queries

/**
 * Fetch metric data using bulk query strategy
 * Always fetches ALL metrics for the store/date range
 * Client-side filtering by metric selection happens in the component
 *
 * @param {Object} params - Query parameters
 * @param {string} params.clientId - Client ID
 * @param {string} params.storeId - Store ID
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {Function} [params.onProgress] - Progress callback (currentCount, hasMore)
 * @param {AbortSignal} [params.signal] - Abort signal for cancellation
 * @returns {Promise<Array>} Array of ALL metric data items (unfiltered)
 * @throws {Error} If query fails or is aborted
 */
export async function fetchMetricData(params) {
  const {
    clientId,
    storeId,
    startDate,
    endDate,
    onProgress,
    signal
  } = params;

  console.log('🔍 [MetricData Query] Input Parameters:', {
    clientId,
    storeId,
    startDate,
    endDate,
    queryStrategy: 'ALL_METRICS (client-filter in component)'
  });

  return await fetchMetricDataBulk(params);
}

/**
 * Fetch data using per-metric queries (efficient for few metrics)
 */
async function fetchMetricDataPerMetric(params) {
  const {
    clientId,
    storeId,
    startDate,
    endDate,
    selectedMetrics,
    onProgress,
    signal
  } = params;

  console.log('🎯 [Per-Metric Query] Fetching', selectedMetrics.length, 'metrics individually');

  let allItems = [];
  let totalPages = 0;

  for (const metricName of selectedMetrics) {
    if (signal?.aborted) {
      throw new Error('Query cancelled');
    }

    console.log(`🎯 [Per-Metric Query] Fetching metric: ${metricName}`);

    let nextToken = null;
    let hasMore = true;

    while (hasMore) {
      if (signal?.aborted) {
        throw new Error('Query cancelled');
      }

      const queryVariables = {
        clientId,
        storeIdDateMetricName: {
          between: [
            { storeId, date: startDate, metricName },
            { storeId, date: endDate, metricName }
          ]
        },
        limit: QUERY_CONFIG.PAGE_SIZE,
        nextToken,
        sortDirection: 'ASC'
      };

      console.log(`🎯 [Per-Metric Query] ${metricName} page ${totalPages + 1} variables:`, queryVariables);

      const response = await API.graphql(
        graphqlOperation(metricDataByClientStoreDateMetric, queryVariables)
      );

      console.log(`🎯 [Per-Metric Query] ${metricName} Full Response:`, {
        fullResponse: response,
        data: response.data,
        items: response.data?.metricDataByClientStoreDateMetric?.items
      });

      const items = response.data.metricDataByClientStoreDateMetric.items || [];
      nextToken = response.data.metricDataByClientStoreDateMetric.nextToken;
      hasMore = !!nextToken;

      console.log(`🎯 [Per-Metric Query] ${metricName} returned ${items.length} items`, {
        hasNextToken: !!nextToken,
        firstItem: items[0],
        lastItem: items[items.length - 1]
      });

      allItems = [...allItems, ...items];
      totalPages++;

      if (onProgress) {
        onProgress(allItems.length, hasMore || selectedMetrics.indexOf(metricName) < selectedMetrics.length - 1, totalPages);
      }
    }
  }

  console.log('✅ [Per-Metric Query] Final Result:', {
    totalItems: allItems.length,
    totalPages,
    metricsQueried: selectedMetrics.length
  });

  return allItems;
}

/**
 * Fetch data using bulk query (fetches ALL metrics, no filtering)
 */
async function fetchMetricDataBulk(params) {
  const {
    clientId,
    storeId,
    startDate,
    endDate,
    onProgress,
    signal
  } = params;

  console.log('📦 [Bulk Query] Fetching ALL metrics (no filter)');

  let allItems = [];
  let nextToken = null;
  let hasMore = true;
  let pageCount = 0;

  while (hasMore) {
    if (signal?.aborted) {
      throw new Error('Query cancelled');
    }

    try {
      const queryVariables = {
        clientId,
        date: {
          between: [startDate, endDate]
        },
        filter: {
          storeId: { eq: storeId }
        },
        limit: QUERY_CONFIG.PAGE_SIZE,
        nextToken,
        sortDirection: 'ASC'
      };

      console.log(`📦 [Bulk Query] Page ${pageCount + 1} Variables:`, queryVariables);

      const response = await API.graphql(
        graphqlOperation(metricDataByClientDate, queryVariables)
      );

      console.log(`📦 [Bulk Query] Page ${pageCount + 1} Response:`, {
        fullResponse: response,
        itemsCount: response.data?.metricDataByClientDate?.items?.length || 0,
        hasNextToken: !!response.data?.metricDataByClientDate?.nextToken,
        firstItem: response.data?.metricDataByClientDate?.items?.[0],
        lastItem: response.data?.metricDataByClientDate?.items?.[response.data?.metricDataByClientDate?.items?.length - 1]
      });

      const items = response.data.metricDataByClientDate.items || [];
      nextToken = response.data.metricDataByClientDate.nextToken;
      hasMore = !!nextToken;

      console.log(`📦 [Bulk Query] Page ${pageCount + 1} Items:`, {
        totalItems: items.length,
        uniqueMetrics: [...new Set(items.map(i => i.metricName))],
        sampleItems: items.slice(0, 3)
      });

      // No filtering - return ALL metrics
      allItems = [...allItems, ...items];
      pageCount++;

      if (onProgress) {
        onProgress(allItems.length, hasMore, pageCount);
      }

      if (signal?.aborted) {
        throw new Error('Query cancelled');
      }

    } catch (error) {
      console.error('❌ [Bulk Query] Error:', error);
      if (signal?.aborted || error.message === 'Query cancelled') {
        throw new Error('Query cancelled');
      }
      throw new Error(`Failed to fetch metric data: ${error.message}`);
    }
  }

  console.log('✅ [Bulk Query] Final Result:', {
    totalItems: allItems.length,
    uniqueMetrics: [...new Set(allItems.map(i => i.metricName))],
    dateRange: {
      start: startDate,
      end: endDate
    }
  });

  return allItems;
}

/**
 * Fetch metric data with retry logic
 * @param {Object} params - Query parameters (same as fetchMetricData)
 * @param {number} [maxRetries=3] - Maximum number of retries
 * @returns {Promise<Array>} Array of metric data items
 * @throws {Error} If all retries fail
 */
export async function fetchMetricDataWithRetry(params, maxRetries = QUERY_CONFIG.MAX_RETRIES) {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetchMetricData(params);
    } catch (error) {
      // Don't retry if cancelled
      if (error.message === 'Query cancelled') {
        throw error;
      }

      lastError = error;

      // Wait before retry with exponential backoff
      if (attempt < maxRetries - 1) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Validate query parameters
 * @param {Object} params - Query parameters
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateQueryParams(params) {
  const { clientId, storeId, startDate, endDate } = params;

  if (!clientId) {
    return { valid: false, error: 'Client ID is required' };
  }

  if (!storeId) {
    return { valid: false, error: 'Store ID is required' };
  }

  if (!startDate || !endDate) {
    return { valid: false, error: 'Date range is required' };
  }

  return { valid: true };
}

/**
 * Create an abort controller for query cancellation
 * @returns {{controller: AbortController, signal: AbortSignal}} Abort controller and signal
 */
export function createQueryCancellation() {
  const controller = new AbortController();
  return {
    controller,
    signal: controller.signal
  };
}
